import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { despesasService } from '../../services/despesas';
import { veiculosService } from '../../services/veiculos';
import { categoriasDespesaService } from '../../services/categoriasDespesa';
import { motoristasService } from '../../services/motoristas';
import { Despesa } from '../../types';
import CurrencyInput from '../../components/CurrencyInput';
import { KmInput, DocumentoInput } from '../../components/MaskedInputs';
import SearchableSelect from '../../components/SearchableSelect';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';
import UploadDeArquivos from '../../components/UploadDeArquivos';
import { documentoValido } from '../../utils/documentos';

// Listas de seleção precisam vir completas: o SearchableSelect filtra em memória,
// então o que não veio na resposta é invisível para a busca do usuário.
const OPCOES_SELECT = { per_page: 500 } as const;

const DespesasForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    veiculo_id: '',
    motorista_id: '',
    data_despesa: '',
    valor: '',
    descricao: '',
    categoria: '',
    tipo: 'variavel',
    status: 'pendente',
    fornecedor: '',
    documento_fornecedor: '',
    data_vencimento: '',
    data_pagamento: '',
    forma_pagamento: '',
    numero_documento: '',
    quilometragem: '',
    observacoes: '',
    parcela_atual: '',
    total_parcelas: '',
    valor_parcela: '',
    comprovantes: [] as string[]
  });

  // Buscar despesa para edição
  const { data: despesaData } = useQuery({
    queryKey: ['despesa', id],
    queryFn: () => despesasService.getDespesa(Number(id)),
    enabled: isEditing
  });

  // Buscar veículos
  const { data: categoriasData } = useQuery({
    queryKey: ['categorias-despesa'],
    queryFn: categoriasDespesaService.listar,
  });

  const { data: veiculosData } = useQuery({
    queryKey: ['veiculos', OPCOES_SELECT],
    queryFn: () => veiculosService.getVeiculos(OPCOES_SELECT)
  });

  // Buscar motoristas
  const { data: motoristasData } = useQuery({
    queryKey: ['motoristas', OPCOES_SELECT],
    queryFn: () => motoristasService.getMotoristas(OPCOES_SELECT)
  });

  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: (data: Partial<Despesa>) => {
      if (isEditing) {
        return despesasService.updateDespesa(Number(id), data);
      } else {
        return despesasService.createDespesa(data);
      }
    },
    onSuccess: () => {
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
      queryClient.invalidateQueries({ queryKey: ['despesas'] });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['despesa', id] });
      }
      toast.success(isEditing ? 'Despesa atualizada com sucesso!' : 'Despesa criada com sucesso!');
      navigate('/despesas');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar despesa');
    }
  });

  // Preencher formulário quando editando
  useEffect(() => {
    if (despesaData?.data) {
      const despesa = despesaData.data;
      setFormData({
        veiculo_id: despesa.veiculo_id?.toString() ?? '',
        motorista_id: despesa.motorista_id?.toString() ?? '',
        data_despesa: despesa.data_despesa ? new Date(despesa.data_despesa).toISOString().split('T')[0] : '',
        valor: despesa.valor?.toString() ?? '',
        descricao: despesa.descricao ?? '',
        categoria: despesa.categoria ?? '',
        tipo: despesa.tipo ?? 'variavel',
        status: despesa.status ?? 'pendente',
        fornecedor: despesa.fornecedor ?? '',
        documento_fornecedor: despesa.documento_fornecedor ?? '',
        data_vencimento: despesa.data_vencimento ? new Date(despesa.data_vencimento).toISOString().split('T')[0] : '',
        data_pagamento: despesa.data_pagamento ? new Date(despesa.data_pagamento).toISOString().split('T')[0] : '',
        forma_pagamento: despesa.forma_pagamento ?? '',
        numero_documento: despesa.numero_documento ?? '',
        quilometragem: despesa.quilometragem?.toString() ?? '',
        observacoes: despesa.observacoes ?? '',
        comprovantes: despesa.comprovantes ?? [],
        parcela_atual: despesa.parcela_atual?.toString() ?? '',
        total_parcelas: despesa.total_parcelas?.toString() ?? '',
        valor_parcela: despesa.valor_parcela?.toString() ?? ''
      });
    }
  }, [despesaData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // O km e opcional na despesa, mas se vier nao pode ser menor que o ultimo
  // registro do veiculo. Nao vem preenchido: despesa nem sempre tem km.
  const { ultimo: ultimoKm, carregando: buscandoKm } = useUltimoKm(formData.veiculo_id);
  const erroKm = erroDeKmMenor(formData.quilometragem, ultimoKm);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditing && erroKm) {
      toast.error(erroKm);
      return;
    }

    // O documento e opcional; se veio preenchido, precisa ser um CPF ou CNPJ de
    // verdade — o servidor recusa, e sem este aviso a recusa chega sem contexto.
    if (formData.documento_fornecedor && !documentoValido(formData.documento_fornecedor)) {
      toast.error('Documento do fornecedor inválido. Informe um CPF ou um CNPJ.');
      return;
    }

    // Converter dados para o formato esperado pela API
    const submitData: Partial<Despesa> = {
      veiculo_id: formData.veiculo_id ? Number(formData.veiculo_id) : undefined,
      motorista_id: formData.motorista_id ? Number(formData.motorista_id) : undefined,
      data_despesa: formData.data_despesa,
      valor: formData.valor ? Number(formData.valor) : undefined,
      descricao: formData.descricao,
      categoria: formData.categoria as any,
      tipo: formData.tipo as any,
      status: formData.status as any,
      fornecedor: formData.fornecedor || undefined,
      documento_fornecedor: formData.documento_fornecedor || undefined,
      data_vencimento: formData.data_vencimento || undefined,
      data_pagamento: formData.data_pagamento || undefined,
      forma_pagamento: formData.forma_pagamento as any || undefined,
      numero_documento: formData.numero_documento || undefined,
      quilometragem: formData.quilometragem ? Number(formData.quilometragem) : undefined,
      observacoes: formData.observacoes || undefined,
      comprovantes: formData.comprovantes.length > 0 ? formData.comprovantes : undefined,
      parcela_atual: formData.parcela_atual ? Number(formData.parcela_atual) : undefined,
      total_parcelas: formData.total_parcelas ? Number(formData.total_parcelas) : undefined,
      valor_parcela: formData.valor_parcela ? Number(formData.valor_parcela) : undefined,
    };

    mutation.mutate(submitData);
  };

  const categorias = categoriasData?.data ?? [];
  const veiculos = veiculosData?.data || [];
  const motoristas = motoristasData?.data || [];

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar despesa' : 'Nova despesa'}
        </h1>
        <p className="text-gray-600">
          {isEditing ? 'Atualize as informações da despesa' : 'Preencha os dados da nova despesa'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SearchableSelect
              label="Veículo"
              required
              placeholder="Selecione um veículo"
              value={formData.veiculo_id}
              onChange={(v) => setFormData(prev => ({ ...prev, veiculo_id: v }))}
              options={veiculos.map((veiculo) => ({
                value: String(veiculo.id),
                label: `${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}`,
              }))}
            />

            <SearchableSelect
              label="Motorista"
              placeholder="Selecione um motorista"
              value={formData.motorista_id}
              onChange={(v) => setFormData(prev => ({ ...prev, motorista_id: v }))}
              options={motoristas.map((motorista) => ({
                value: String(motorista.id),
                label: motorista.nome,
              }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data da Despesa *
              </label>
              <input
                type="date"
                name="data_despesa"
                value={formData.data_despesa}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <CurrencyInput
              name="valor"
              value={formData.valor}
              onChange={handleInputChange}
              label="Valor"
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição *
              </label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {/* A lista vem do catalogo da empresa: era fixa no codigo, e
                    quem nao achava a sua lancava tudo em "Outros". */}
                <option value="">Selecione a categoria</option>
                {categorias.map((categoria) => (
                  <option key={categoria.id} value={categoria.slug}>
                    {categoria.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo *
              </label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="fixa">Fixa</option>
                <option value="variavel">Variável</option>
                <option value="eventual">Eventual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="pendente">Pendente</option>
                <option value="paga">Paga</option>
                <option value="vencida">Vencida</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornecedor
              </label>
              <input
                type="text"
                name="fornecedor"
                value={formData.fornecedor}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informações de Pagamento</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Vencimento
              </label>
              <input
                type="date"
                name="data_vencimento"
                value={formData.data_vencimento}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Pagamento
              </label>
              <input
                type="date"
                name="data_pagamento"
                value={formData.data_pagamento}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forma de Pagamento
              </label>
              <select
                name="forma_pagamento"
                value={formData.forma_pagamento}
                onChange={handleInputChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="">Selecione</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="debito">Débito</option>
                <option value="credito">Crédito</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="transferencia">Transferência</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número do Documento
              </label>
              <input
                type="text"
                name="numero_documento"
                value={formData.numero_documento}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Adicionais</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <KmInput
                label="Quilometragem"
                name="quilometragem"
                value={formData.quilometragem}
                onChange={handleInputChange}
              />
              <DicaUltimoKm ultimo={ultimoKm} erro={isEditing ? null : erroKm} carregando={buscandoKm} />
            </div>

            <div>
              {/* Fornecedor pode ser pessoa fisica ou juridica: o componente escolhe
                  a mascara pela quantidade de digitos e persiste so numeros. */}
              <DocumentoInput
                name="documento_fornecedor"
                label="Documento do Fornecedor"
                value={formData.documento_fornecedor}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formData.documento_fornecedor && !documentoValido(formData.documento_fornecedor) && (
                <p className="mt-1 text-xs text-red-600">Documento inválido. Informe um CPF ou um CNPJ.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parcela Atual
              </label>
              <input
                type="number"
                min="1"
                name="parcela_atual"
                value={formData.parcela_atual}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total de Parcelas
              </label>
              <input
                type="number"
                min="1"
                name="total_parcelas"
                value={formData.total_parcelas}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <CurrencyInput
              name="valor_parcela"
              value={formData.valor_parcela}
              onChange={handleInputChange}
              label="Valor da Parcela"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Nota de servico costuma vir em mais de uma folha; guardar so uma
              obrigava a montar colagem ou deixar o resto de fora. */}
          <div className="mt-6">
            <UploadDeArquivos
              label="Comprovantes"
              valor={formData.comprovantes}
              onChange={(comprovantes) => setFormData(prev => ({ ...prev, comprovantes }))}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/despesas')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {mutation.isPending ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DespesasForm;
